import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
  Stack,
} from '@mui/material';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevOpen) => !prevOpen);
  };

  const navItems = [
    { name: 'About', href: '/#about' },
    { name: 'Services', href: '/#services' },
    { name: 'Products', href: '/#product' },
    { name: 'Contact', href: '/#contact' },
  ];

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        height: '100%',
        background: 'linear-gradient(180deg, #ffffff 0%, #eef4fb 100%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          component="img"
          src="logo512.png"
          alt="QuantumMind Logo"
          sx={{
            height: 40,
            width: 40,
          }}
        />
        <Typography variant="h6" component="div" sx={{ fontWeight: 800 }}>
          QuantumMind Software
        </Typography>
      </Box>
      <List sx={{ px: 1.5, py: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component="a"
              href={item.href}
              sx={{
                borderRadius: 3,
                py: 1.4,
                px: 2,
                mb: 0.75,
              }}
            >
              <ListItemText
                primary={item.name}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.88)',
        color: '#10233f',
        backdropFilter: 'blur(18px)',
        borderBottom: '1px solid rgba(16, 35, 63, 0.08)',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: { xs: 76, md: 84 },
            px: { xs: 0, sm: 0 },
          }}
        >
          <Box
            component="a"
            href="/#about"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src="/logo512.png"
              alt="QuantumMind Logo"
              sx={{
                height: { xs: 42, md: 48 },
                width: { xs: 42, md: 48 },
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            />
            <Stack spacing={0.1}>
              <Typography
                variant="body2"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  fontSize: '0.68rem',
                }}
              >
                AI • Cloud • Web
              </Typography>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.98rem', sm: '1.08rem', md: '1.2rem' },
                  lineHeight: 1.1,
                }}
              >
                QuantumMind Software
              </Typography>
            </Stack>
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{
                display: { md: 'none' },
                border: '1px solid rgba(16, 35, 63, 0.1)',
                borderRadius: 3,
              }}
            >
              <Box
                component="div"
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                }}
              >
                <Box sx={{ height: 2, width: 24, backgroundColor: 'currentColor', borderRadius: 2 }} />
                <Box sx={{ height: 2, width: 24, backgroundColor: 'currentColor', borderRadius: 2 }} />
                <Box sx={{ height: 2, width: 24, backgroundColor: 'currentColor', borderRadius: 2 }} />
              </Box>
            </IconButton>
          ) : (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.name}
                  component="a"
                  href={item.href}
                  sx={{
                    color: '#163055',
                    minWidth: 'auto',
                    px: 1.75,
                    py: 1,
                    fontWeight: 600,
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      width: '0',
                      height: '2px',
                      bottom: 6,
                      left: '50%',
                      backgroundColor: theme.palette.secondary.main,
                      transition: 'all 0.3s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(20, 50, 92, 0.05)',
                      '&:after': {
                        width: '72%',
                        left: '14%',
                      },
                    },
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
          )}
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Header;
